import {
  atualizarClienteSchema,
  brandGuidelinesSchema,
  clienteSchema,
  criarClienteSchema,
  criarDataComemorativaSchema,
  dataComemorativaSchema,
  dossieSchema,
  dossieVersaoSchema,
  listaDeClientesSchema,
  listaDeDatasComemorativasSchema,
  listaDeDossieVersoesSchema,
  metaConexaoSchema,
  metaHealthSchema,
  listaMetaPaginasSchema,
  listaContasSociaisSchema,
  loginTikTokIniciadoSchema,
  statusLoginTikTokSchema,
  volumetriaSchema,
  type AtualizarBrandGuidelines,
  type AtualizarCliente,
  type AtualizarDossie,
  type AtualizarVolumetria,
  type BrandGuidelines,
  type Cliente,
  type CriarCliente,
  type CriarDataComemorativa,
  type DataComemorativa,
  type Dossie,
  type DossieVersao,
  type ListaDeClientes,
  type MetaConexao,
  type MetaHealth,
  type MetaPaginaDisponivel,
  type LoginTikTokIniciadoDto,
  type PlataformaDePublicacaoDto,
  type StatusLoginTikTokDto,
  type Volumetria,
} from "@base/contracts";
import { z } from "zod";
import type { ClienteHttp } from "../../../shared/http/cliente-http";

export type ListaContasSociais = z.infer<typeof listaContasSociaisSchema>;

type PontoNaTelaTikTok = { x: number; y: number };

/** Confirmação do TikTok: atalho por método ou o que a pessoa fez no print. */
export type AcaoLoginTikTokWeb = {
  metodo?: "email" | "sms" | "senha";
  codigo?: string;
  senha?: string;
  tela?:
    | { tipo: "clique"; ponto: PontoNaTelaTikTok }
    | { tipo: "arrasto"; de: PontoNaTelaTikTok; para: PontoNaTelaTikTok }
    | { tipo: "texto"; texto: string; enter: boolean }
    | { tipo: "tecla"; tecla: "Enter" | "Backspace" | "Tab" };
};

export type ClientesGateway = {
  listar(consulta?: {
    readonly q?: string;
    readonly status?: "ativo" | "inativo" | "todos";
  }): Promise<ListaDeClientes>;
  atualizarStatus(
    clienteId: string,
    status: "ativo" | "inativo",
  ): Promise<Cliente>;
  obterAtual(): Promise<Cliente>;
  criar(corpo: CriarCliente): Promise<Cliente>;
  atualizarAtual(corpo: AtualizarCliente): Promise<Cliente>;
  obterBrand(): Promise<BrandGuidelines>;
  atualizarBrand(corpo: AtualizarBrandGuidelines): Promise<BrandGuidelines>;
  listarDatas(mes?: number): Promise<readonly DataComemorativa[]>;
  criarData(corpo: CriarDataComemorativa): Promise<DataComemorativa>;
  removerData(id: string): Promise<void>;
  obterVolumetria(): Promise<Volumetria>;
  atualizarVolumetria(corpo: AtualizarVolumetria): Promise<Volumetria>;
  obterDossie(): Promise<Dossie>;
  atualizarDossie(corpo: AtualizarDossie): Promise<Dossie>;
  listarVersoesDossie(): Promise<readonly DossieVersao[]>;
  salvarVersaoDossie(): Promise<DossieVersao>;
  restaurarVersaoDossie(id: string): Promise<Dossie>;
  obterMetaConexao(): Promise<MetaConexao>;
  iniciarMetaOAuth(): Promise<{ url: string }>;
  desvincularMeta(): Promise<MetaConexao>;
  obterMetaHealth(): Promise<MetaHealth>;
  listarPaginasMeta(): Promise<readonly MetaPaginaDisponivel[]>;
  selecionarPaginaMeta(
    facebookPageId: string,
    modo?: "instagram" | "pagina" | "pagina_e_instagram",
  ): Promise<MetaConexao>;
  limparPaginaMeta(): Promise<MetaConexao>;
  listarContasSociais(): Promise<ListaContasSociais>;
  iniciarTwitterOAuth(): Promise<{ url: string }>;
  iniciarTikTokOAuth(): Promise<{ url: string }>;
  iniciarLoginTikTok(): Promise<LoginTikTokIniciadoDto>;
  verificarLoginTikTok(
    desafioId: string,
    acao?: AcaoLoginTikTokWeb,
  ): Promise<StatusLoginTikTokDto>;
  desvincularContaSocial(
    plataforma: PlataformaDePublicacaoDto,
  ): Promise<void>;
};

export class ClientesGatewayHttp implements ClientesGateway {
  constructor(private readonly http: ClienteHttp) {}

  listar(consulta?: {
    readonly q?: string;
    readonly status?: "ativo" | "inativo" | "todos";
  }): Promise<ListaDeClientes> {
    return this.http.requisitar("/v1/clientes", listaDeClientesSchema, {
      consulta: {
        ...(consulta?.q ? { q: consulta.q } : {}),
        ...(consulta?.status ? { status: consulta.status } : {}),
      },
    });
  }

  async atualizarStatus(
    clienteId: string,
    status: "ativo" | "inativo",
  ): Promise<Cliente> {
    return this.http.requisitar(
      `/v1/clientes/${clienteId}/status`,
      clienteSchema,
      { metodo: "PATCH", corpo: { status } },
    );
  }

  obterAtual(): Promise<Cliente> {
    return this.http.requisitar("/v1/clientes/atual", clienteSchema);
  }

  criar(corpo: CriarCliente): Promise<Cliente> {
    const validado = criarClienteSchema.parse(corpo);
    return this.http.requisitar("/v1/clientes", clienteSchema, {
      metodo: "POST",
      corpo: validado,
    });
  }

  atualizarAtual(corpo: AtualizarCliente): Promise<Cliente> {
    const validado = atualizarClienteSchema.parse(corpo);
    return this.http.requisitar("/v1/clientes/atual", clienteSchema, {
      metodo: "PATCH",
      corpo: validado,
    });
  }

  obterBrand(): Promise<BrandGuidelines> {
    return this.http.requisitar(
      "/v1/clientes/atual/brand-guidelines",
      brandGuidelinesSchema,
    );
  }

  atualizarBrand(
    corpo: AtualizarBrandGuidelines,
  ): Promise<BrandGuidelines> {
    return this.http.requisitar(
      "/v1/clientes/atual/brand-guidelines",
      brandGuidelinesSchema,
      { metodo: "PATCH", corpo },
    );
  }

  async listarDatas(mes?: number): Promise<readonly DataComemorativa[]> {
    const resp = await this.http.requisitar(
      "/v1/clientes/atual/datas-comemorativas",
      listaDeDatasComemorativasSchema,
      mes !== undefined ? { consulta: { mes } } : {},
    );
    return resp.itens;
  }

  criarData(corpo: CriarDataComemorativa): Promise<DataComemorativa> {
    const validado = criarDataComemorativaSchema.parse(corpo);
    return this.http.requisitar(
      "/v1/clientes/atual/datas-comemorativas",
      dataComemorativaSchema,
      { metodo: "POST", corpo: validado },
    );
  }

  async removerData(id: string): Promise<void> {
    await this.http.requisitar(
      `/v1/clientes/atual/datas-comemorativas/${id}`,
      z.null(),
      { metodo: "DELETE" },
    );
  }

  obterVolumetria(): Promise<Volumetria> {
    return this.http.requisitar(
      "/v1/clientes/atual/volumetria",
      volumetriaSchema,
    );
  }

  atualizarVolumetria(corpo: AtualizarVolumetria): Promise<Volumetria> {
    return this.http.requisitar(
      "/v1/clientes/atual/volumetria",
      volumetriaSchema,
      { metodo: "PATCH", corpo },
    );
  }

  obterDossie(): Promise<Dossie> {
    return this.http.requisitar("/v1/clientes/atual/dossie", dossieSchema);
  }

  atualizarDossie(corpo: AtualizarDossie): Promise<Dossie> {
    return this.http.requisitar("/v1/clientes/atual/dossie", dossieSchema, {
      metodo: "PATCH",
      corpo,
    });
  }

  async listarVersoesDossie(): Promise<readonly DossieVersao[]> {
    const resp = await this.http.requisitar(
      "/v1/clientes/atual/dossie/versoes",
      listaDeDossieVersoesSchema,
    );
    return resp.itens;
  }

  salvarVersaoDossie(): Promise<DossieVersao> {
    return this.http.requisitar(
      "/v1/clientes/atual/dossie/versoes",
      dossieVersaoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  restaurarVersaoDossie(id: string): Promise<Dossie> {
    return this.http.requisitar(
      `/v1/clientes/atual/dossie/versoes/${id}/restaurar`,
      dossieSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  obterMetaConexao(): Promise<MetaConexao> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta-conexao",
      metaConexaoSchema,
    );
  }

  iniciarMetaOAuth(): Promise<{ url: string }> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta/iniciar",
      z.object({ url: z.string().url() }),
      { metodo: "POST", corpo: {} },
    );
  }

  desvincularMeta(): Promise<MetaConexao> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta/desvincular",
      metaConexaoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  obterMetaHealth(): Promise<MetaHealth> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta-health",
      metaHealthSchema,
    );
  }

  async listarPaginasMeta(): Promise<readonly MetaPaginaDisponivel[]> {
    const resp = await this.http.requisitar(
      "/v1/clientes/atual/meta/paginas",
      listaMetaPaginasSchema,
    );
    return resp.itens;
  }

  selecionarPaginaMeta(
    facebookPageId: string,
    modo: "instagram" | "pagina" | "pagina_e_instagram" = "instagram",
  ): Promise<MetaConexao> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta/selecionar-pagina",
      metaConexaoSchema,
      { metodo: "POST", corpo: { facebookPageId, modo } },
    );
  }

  limparPaginaMeta(): Promise<MetaConexao> {
    return this.http.requisitar(
      "/v1/clientes/atual/meta/limpar-pagina",
      metaConexaoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  listarContasSociais() {
    return this.http.requisitar(
      "/v1/clientes/atual/contas-sociais",
      listaContasSociaisSchema,
    );
  }

  iniciarTwitterOAuth(): Promise<{ url: string }> {
    return this.http.requisitar(
      "/v1/clientes/atual/twitter/iniciar",
      z.object({ url: z.string().url() }),
      { metodo: "POST", corpo: {} },
    );
  }

  iniciarTikTokOAuth(): Promise<{ url: string }> {
    return this.http.requisitar(
      "/v1/clientes/atual/tiktok/iniciar",
      z.object({ url: z.string().url() }),
      { metodo: "POST", corpo: {} },
    );
  }

  iniciarLoginTikTok(): Promise<LoginTikTokIniciadoDto> {
    return this.http.requisitar(
      "/v1/clientes/atual/tiktok/login",
      loginTikTokIniciadoSchema,
      { metodo: "POST", corpo: {} },
    );
  }

  verificarLoginTikTok(
    desafioId: string,
    acao?: AcaoLoginTikTokWeb,
  ): Promise<StatusLoginTikTokDto> {
    return this.http.requisitar(
      "/v1/clientes/atual/tiktok/login/verificar",
      statusLoginTikTokSchema,
      {
        metodo: "POST",
        corpo: {
          desafioId,
          ...(acao?.metodo ? { metodo: acao.metodo } : {}),
          ...(acao?.codigo ? { codigo: acao.codigo } : {}),
          ...(acao?.senha ? { senha: acao.senha } : {}),
          ...(acao?.tela ? { tela: acao.tela } : {}),
        },
      },
    );
  }

  async desvincularContaSocial(
    plataforma: PlataformaDePublicacaoDto,
  ): Promise<void> {
    await this.http.requisitar(
      `/v1/clientes/atual/contas-sociais/${plataforma}/desvincular`,
      z.null(),
      { metodo: "POST", corpo: {} },
    );
  }
}
